import { dataLayerConfig } from '@utils';

export type TQuestion = {
	question: string;
	questionId: string;
	required: boolean;
	type: 'MultiAnswer' | 'SingleAnswer';
	description?: string;
	eyebrow: string;
	options: Array<TOption>;
	validationMessage: string;
	answer: TOption | Array<TOption> | null;
	disclaimer?: {
		title: string;
		description: string;
		link?: string;
		linkText?: string;
	};
	disclaimerPosition?: 'top' | 'bottom';
};

export type TOption = {
	id: string;
	value: string;
};

export type TUser = {
	label: string;
	name: string;
	required: boolean;
	unique_label: string;
	type: 'Text' | 'Phone' | 'Checkbox';
	value: null | string;
	maxLength?: number;
	span?: number;
};

export type TQuestions = Array<TQuestion>;
export type TUsers = Array<TUser>;
export type TPartialOrFullPath = 'Partial' | 'Full';

export type TFormData = {
	form: {
		brand: string;
		form_title: string;
		product_line: string;
		form_description: string;
		form_type: string;
		form_channel: string;
		session_id: string | null;
		form_questions: TQuestions;
		user_form: {
			title: string;
			description: string;
			isSignatureEliteResult: boolean;
			fields: TUsers;
			submitButtonLabel: string;
			legalMessage: string;
			verifyZipCode?: boolean;
			enableStepper?: boolean;
			fieldSteps?: string[][];
			stepTitles?: string[];
			stepDescriptions?: string[];
			disclaimer?: {
				title: string;
				description: string;
				link?: string;
				linkText?: string;
			};
			disclaimerPosition?: 'top' | 'bottom';
		};
		formHeightAuto?: boolean;
		isAutoAdvanceEnabled?: boolean;
		schedulingSegment?: string;
	};
};

export interface IStateObject {
	activeQuestionIndex: number;
	formData: TFormData | null;
	formType: 'Questions' | 'UserDetails' | 'DynamicForm';
	fieldHasError: boolean;
}

export type APIFormDataAD = {
	form: {
		brand: string;
		product_line: string;
		form_title: string;
		form_type: string;
		form_result: string;
		session_id: string;
		form_channel: string;
		form_questions: Array<{
			question: string;
			answer: string;
			unique_label?: string;
		}>;
	};
};

export const getPartialOrFullPath = (formQuestions: TQuestions) => {
	const teethCountQuestion = formQuestions.find((formQuestion) => formQuestion.questionId === 'teethCount');
	if ((teethCountQuestion?.answer as TOption)?.value === '4 or less') {
		return 'Partial';
	}
	return 'Full';
};

const getAspenDentalResult = (
	formQuestions: TQuestions,
	isPartialPathOrFullPath: TPartialOrFullPath,
	isSignatureEliteResult: boolean
) => {
	const preventingCareQuestion = formQuestions.find((formQuestion) => formQuestion.questionId === 'preventingCare');
	const importantSolutionQuestion = formQuestions.find(
		(formQuestion) => formQuestion.questionId === 'importantSolution'
	);
	const isBudgetPath =
		(preventingCareQuestion?.answer as TOption)?.value === 'The cost seems too high' &&
		(importantSolutionQuestion?.answer as TOption)?.value ===
			'Most budget-friendly option with flexible ways to pay';

	if (isBudgetPath) {
		if (isPartialPathOrFullPath === 'Partial') {
			return Partial_Denture_Result;
		}
		return isSignatureEliteResult ? Signature_Elite_Result : Comfilytes_Denture_Result;
	}
	if (isPartialPathOrFullPath === 'Partial') {
		return Dental_Implants_Result;
	}
	return Implant_Dentures_Result;
};

export const getResultType = (
	formQuestions: TQuestions,
	isPartialPathOrFullPath: TPartialOrFullPath,
	isSignatureEliteResult = false
) => {
	return getAspenDentalResult(formQuestions, isPartialPathOrFullPath, isSignatureEliteResult);
};

export const Partial_Denture_Result = {
	title: 'partial',
	resultPage: '/patient-resources/quiz-results/partial-dentures/',
};

export const Comfilytes_Denture_Result = {
	title: 'comfilytes',
	resultPage: '/patient-resources/quiz-results/comfilytes-dentures/',
};

export const Dental_Implants_Result = {
	title: 'implant',
	resultPage: '/patient-resources/quiz-results/single-tooth-implant-and-implant-bridge/',
};

export const Signature_Elite_Result = {
	title: 'signature elite',
	resultPage: '/patient-resources/quiz-results/signature-elite-dentures/',
};

export const Implant_Dentures_Result = {
	title: 'implant denture',
	resultPage: '/patient-resources/quiz-results/implant-dentures/',
};

export const quizGA4DataLayerPush = (brand: string, event: string, formResult: string, formData: TFormData) => {
	dataLayerConfig({
		event: 'formSubmission',
		form_data: {
			brand: brand,
			product_line: formData.form.product_line,
			submission_date: new Date().toISOString(),
			form_title: formData.form.form_title,
			form_description: formData.form.form_description,
			form_type: formData.form.form_type,
			form_result: formResult,
			form_step: event,
		},
	});
};

export const scrollElementIntoView = (containerRef: React.RefObject<HTMLElement>): void => {
	if (!containerRef.current) return;

	const headerElement = document.querySelector('header');
	const headerHeight = headerElement?.getBoundingClientRect().height ?? 0;

	const isElementInView = (): boolean => {
		const rect = containerRef.current?.getBoundingClientRect();
		if (!rect) return true;
		return rect.top >= headerHeight + 20;
	};

	if (!isElementInView()) {
		globalThis.window.scrollTo({
			top: containerRef.current.getBoundingClientRect().top + globalThis.window.scrollY - headerHeight - 20,
			behavior: 'smooth',
		});
	}
};
